import React, { useState, useMemo, useEffect } from 'react';
import { Hotel, TravellerInfo, Place } from '../../types/Quotation';
import AsyncSelect from 'react-select/async';
import { LocationOption } from '../../types/Location';
import { loadCityLocations } from '../../api/Location';
import { useHotels } from '../../hooks/useHotels';
import '../../css/HotelListModal.css';

interface HotelListModalProps {
  place: Place;
  checkIn: string;
  checkOut: string;
  travellers: TravellerInfo;
  onClose: () => void;
  onSelect: (hotel: Hotel) => void;
  initialHotels?: Hotel[];
  initialLoading?: boolean;
  initialError?: any;
}

const HotelListModal: React.FC<HotelListModalProps> = ({
  place,
  checkIn: defaultCheckIn,
  checkOut: defaultCheckOut,
  travellers,
  onClose,
  onSelect,
  initialHotels = [],
  initialLoading = false,
  initialError = null,
}) => {
  const [city, setCity] = useState<LocationOption>({ label: place.label, value: place.value, type: 'city' });
  const [checkIn, setCheckIn] = useState(defaultCheckIn);
  const [checkOut, setCheckOut] = useState(defaultCheckOut);

  const [sortBy, setSortBy] = useState<'price' | 'rating'>('price');
  const [filterStar, setFilterStar] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [searchKey, setSearchKey] = useState<number>(0);
  const [hotelNameInput, setHotelNameInput] = useState<string>('');
  const [mealType] = useState<string>(''); // keep as empty string to match expected payload

  // Ensure we use a proper city code from Location API
  // Only run this when the modal first opens or when city label changes significantly
  useEffect(() => {
    const val = (city?.value || '').trim();
    const isAirport = /^[A-Za-z]{3}$/.test(val);
    const isCityId = /^\d+$/.test(val);
    if (isAirport || isCityId) return; // already a code/id
    const label = city?.label?.trim();
    if (!label || label.length < 3) return; // Don't search for very short labels
    
    // Only resolve city if we don't have a valid code and haven't searched yet
    if (searchKey === 0) {
      const timeoutId = setTimeout(() => {
        let cancelled = false;
        loadCityLocations(label).then(opts => {
          if (cancelled) return;
          const match = (opts || []).find(o => o.type === 'city' && o.label?.toLowerCase().includes(label.toLowerCase()));
          if (match && match.value !== city?.value) setCity(match);
        }).catch(() => void 0);
        return () => { cancelled = true; };
      }, 300); // Debounce for 300ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [city?.label, city?.value, searchKey]);

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    StarRating: filterStar, // send 0 as well
    MealType: mealType,
    HotelName: hotelNameInput.trim() || undefined,
  }), [filterStar, mealType, hotelNameInput]);

  const hookResult = useHotels(
    (city?.value || '').trim(),
    checkIn,
    checkOut,
    travellers,
    page,
    limit,
    filters,
    searchKey
  );

  const hotels = initialHotels.length ? initialHotels : hookResult.hotels;
  const loading = initialHotels.length ? initialLoading : hookResult.loading;
  const error   = initialHotels.length ? initialError   : hookResult.error;
  const pagination = hookResult.pagination;

  // Track room type per visible hotel card (by index in current list)
  const [roomTypes, setRoomTypes] = useState<Record<number, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<Record<number, any>>({});

  const extractVariants = (h: any): Array<{ id?: string; name: string; price?: number; currency?: string; boardBasis?: string; refundable?: boolean; taxesIncluded?: boolean }> => {
    // Prefer `roomVariants` as in TBO response; fallback to other common shapes
    const candidates: any[] =
      (Array.isArray(h?.roomVariants) && h.roomVariants) ||
      (Array.isArray(h?.variants) && h.variants) ||
      (Array.isArray(h?.rooms) && h.rooms) ||
      (Array.isArray(h?.roomTypes) && h.roomTypes) ||
      (Array.isArray(h?.room_types) && h.room_types) ||
      (Array.isArray(h?.availableRooms) && h.availableRooms) ||
      (Array.isArray(h?.ratePlans) && h.ratePlans) ||
      [];
    return candidates
      .map((v: any) => {
        const name = v?.name || v?.roomType || v?.title || v?.type || '';
        const price = typeof v?.price === 'number' ? v.price : (typeof v?.amount === 'number' ? v.amount : (typeof v?.rate === 'number' ? v.rate : undefined));
        const currency = v?.currency || h?.currency;
        const id = v?.id || v?.code || undefined;
        const boardBasis = v?.boardBasis;
        const refundable = typeof v?.refundable === 'boolean' ? v.refundable : undefined;
        const taxesIncluded = typeof v?.taxesIncluded === 'boolean' ? v.taxesIncluded : undefined;
        return name ? { id, name, price, currency, boardBasis, refundable, taxesIncluded } : null;
      })
      .filter(Boolean) as Array<{ id?: string; name: string; price?: number; currency?: string; boardBasis?: string; refundable?: boolean; taxesIncluded?: boolean }>;
  };

  // Helper: decide if a variant includes breakfast via board basis or inclusions
  const hasBreakfast = (v: any): boolean => {
    const basisRaw = v?.boardBasis || v?.board_basis || '';
    const basis = typeof basisRaw === 'string' ? basisRaw.toLowerCase() : '';
    const incs: string[] = Array.isArray(v?.inclusions) ? v.inclusions : [];
    const incsText = incs.join(' ').toLowerCase();
    // Common codes: CP = Continental Plan (breakfast), MAP/AP include breakfast, half/full board include breakfast
    const basisHas = ['breakfast', 'with_breakfast', 'bed_and_breakfast', 'bed & breakfast', 'cp', 'map', 'ap', 'half_board', 'full_board', 'all_inclusive']
      .some(key => basis.includes(key));
    const inclusionsHas = /break\s*fast|breakfast|complimentary\s*breakfast|free\s*breakfast/i.test(incsText);
    return basisHas || inclusionsHas;
  };

  // Pick default variant: lowest price that includes breakfast; else absolute lowest price
  const pickDefaultVariant = (variants: any[]): any | undefined => {
    if (!variants || variants.length === 0) return undefined;
    const numericPrice = (v: any) => (typeof v?.price === 'number' ? v.price : Number.POSITIVE_INFINITY);
    const withBreakfast = variants.filter(hasBreakfast);
    const pool = withBreakfast.length ? withBreakfast : variants;
    return pool.slice().sort((a, b) => numericPrice(a) - numericPrice(b))[0];
  };

  // Auto-select default variant per hotel when results change (do not override user choice)
  useEffect(() => {
    try {
      if (!Array.isArray(hotels) || hotels.length === 0) return;
      const nextSel: Record<number, any> = { ...selectedVariant };
      const nextTypes: Record<number, string> = { ...roomTypes };
      let changed = false;
      hotels.forEach((h, i) => {
        if (nextSel[i]) return; // respect user selection
        const vars = extractVariants(h);
        if (!vars || vars.length === 0) return;
        const def = pickDefaultVariant(vars);
        if (def) {
          nextSel[i] = def;
          nextTypes[i] = (def.id ?? def.name ?? '') as string;
          changed = true;
        }
      });
      if (changed) {
        setSelectedVariant(nextSel);
        setRoomTypes(nextTypes);
      }
    } catch {}
    // Only react to list changes; do not include selectedVariant/roomTypes to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels]);

  // No children age inputs in modal; API will receive empty list when not provided

  const visibleHotels = useMemo(() => {
    let list = hotels;
    if (filterStar > 0) {
      list = list.filter(h => Number(h.starRating) >= filterStar);
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'price') {
        return a.price - b.price;
      } else {
        return Number(b.starRating) - Number(a.starRating);
      }
    });
  }, [hotels, sortBy, filterStar]);

  // Removed debouncing — search triggers only via button click

  return (
    <div className="modal-backdrop">
      <div className="modal hotel-modal">
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="hotel-search-form">
          <div className="hs-field">
            <label>City</label>
            <AsyncSelect<LocationOption, false>
              cacheOptions
              defaultOptions
              loadOptions={loadCityLocations}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              value={city}
              onChange={opt => setCity(opt as LocationOption)}
              placeholder="Type or select a city"
              classNamePrefix="rs"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                menu: (base) => ({ ...base, zIndex: 2000 }),
                control: (base) => ({ ...base, minHeight: 36, borderColor: '#ccc', boxShadow: 'none' }),
                valueContainer: (base) => ({ ...base, padding: '0 8px' }),
              }}
            />
          </div>
          <div className="hs-field">
            <label>Check‑in</label>
            <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)} />
          </div>
          <div className="hs-field">
            <label>Check‑out</label>
            <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)} />
          </div>
          {/* Children age inputs removed per requirements */}
          <button
            className="hs-search-btn"
            onClick={() => {
              setPage(1);
              setSearchKey(k => k + 1);
            }}
          >
            Search
          </button>
        </div>

        <div className="hotel-controls">
          <div className="hc-sort">
            <span>Sort by:</span>
            <button
              className={sortBy === 'price' ? 'active' : ''}
              onClick={() => setSortBy('price')}
            >
              Price
            </button>
            <button
              className={sortBy === 'rating' ? 'active' : ''}
              onClick={() => setSortBy('rating')}
            >
              Rating
            </button>
          </div>
          <div className="hc-filter">
            <label>Name:</label>
            <input
              type="text"
              value={hotelNameInput}
              onChange={e => setHotelNameInput(e.target.value)}
              placeholder="Search by hotel name"
            />
          </div>
          <div className="hc-filter">
            <label>Min Stars:</label>
            <select
              value={filterStar}
              onChange={e => {
                setFilterStar(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={0}>All</option>
              {[5,4,3,2,1].map(s => (
                <option key={s} value={s}>{s}★+</option>
              ))}
            </select>
          </div>
          <div className="hc-filter">
            <label>Page size:</label>
            <select
              value={limit}
              onChange={e => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10,20,30,50,100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="hotel-list">
          {loading && <p className="loading">Loading hotels…</p>}
          {error && <p className="error">{error.message}</p>}
          {!loading && !error && visibleHotels.map((h, i) => (
            <div className="hotel-card" key={i}>
              <div className="hotel-main">
                <img
                  src={h.thumbnail || '/placeholder-hotel.png'}
                  alt={h.name}
                  className="hotel-thumbnail"
                />
                <div className="hotel-info">
                  <h3 className="hotel-name">{h.name}</h3>
                  <div className="hotel-stars">
                    {Array.from({ length: Number(h.starRating) })
                      .map(() => '★')
                      .join('')}
                  </div>
                  {h.address && (
                    <div className="hotel-address" title={h.address}>{h.address}</div>
                  )}
                  <div className="hotel-amenities">
                    {(h.amenities ?? []).slice(0, 3).map((amenity, idx) => (
                      <span className="amenity-badge" key={idx}>
                        {amenity}
                      </span>
                    ))}
                    {(h.amenities ?? []).length > 3 && (
                      <span className="amenity-more">
                        +{h.amenities.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                {(() => {
                  const variants = extractVariants(h);
                  const selected = selectedVariant[i];
                  const displayPrice = (selected?.price ?? h.price) as number;
                  const currency = (selected?.currency || h.currency || 'INR') as string;
                  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';
                  return (
                    <div className="hotel-rate" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <span className="hotel-price">{symbol}{Number(displayPrice || 0).toLocaleString('en-IN')}</span>
                      <select
                        value={roomTypes[i] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRoomTypes(prev => ({ ...prev, [i]: value }));
                          const next = variants.find(v => (v.id ?? v.name) === value);
                          setSelectedVariant(prev => ({ ...prev, [i]: next }));
                        }}
                        title="Select room type"
                        style={{ minWidth: 140 }}
                      >
                        <option value="">Select room type</option>
                        {variants.map((v, idx) => {
                          const labelCurrency = v.currency || currency;
                          const labelSymbol = labelCurrency === 'USD' ? '$' : labelCurrency === 'EUR' ? '€' : '₹';
                          // Normalize board basis text
                          const basis = typeof v.boardBasis === 'string' ? v.boardBasis.replace(/_/g, ' ') : undefined;
                          const parts = [v.name, basis].filter(Boolean).join(' — ');
                          const pricePart = typeof v.price === 'number' ? ` — ${labelSymbol}${v.price.toLocaleString('en-IN')}` : '';
                          return (
                            <option key={v.id ?? idx} value={v.id ?? v.name}>
                              {parts}{pricePart}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })()}
              </div>
              <div className="hotel-action" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="select-btn"
                  onClick={() => {
                    const sv = selectedVariant[i];
                    const chosenType = (sv?.name || roomTypes[i] || '').trim() || undefined;
                    const chosenPrice = typeof sv?.price === 'number' ? sv.price : h.price;
                    const chosenId = sv?.id as string | undefined;
                    onSelect({ ...h, price: chosenPrice, roomType: chosenType, roomVariantId: chosenId, roomVariant: sv });
                  }}
                  title="Select hotel with chosen room type"
                >
                  Select
                </button>
              </div>
            </div>
          ))}
        </div>
        {!loading && !error && pagination && (
          <div className="hotel-pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="page-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} results)
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelListModal;
